import React, { useState, useEffect, useCallback } from "react";
import { Form, Modal, ModalBody, ModalHeader } from "reactstrap";
import Flatpickr from "react-flatpickr";
import { useFormik } from "formik";
import * as Yup from "yup";
import { RequiredField } from "../../utils/helpers/validationMasseges";
import { getId, getName, getRole } from "../../utils/helpers/custom";
import { addLeave, applyCompOffLeave, editLeave } from "../../api/leaveApi";
import {
  LEAVE_CATEGORIES,
  LEAVE_TYPES,
  ResponseStatusEnum,
} from "../../utils/helpers/enums";
import { toast } from "react-toastify";
import {
  dateFormatChange,
  getInputPlaceholder,
  getSelectPlaceholder,
  Roles,
} from "../../utils/helpers/common";
import { useLocation } from "react-router-dom";
import BaseRadioGroup from "../../base/BaseRadioGroup";
import BaseInput from "../../base/baseInput";
import { listOfData, listOfEmployee } from "../../api/commonApi";
import {
  ACQUIRED_DATE,
  ADMIN,
  APPLY_LEAVE,
  BDE,
  COL,
  DATES,
  EMPLOYEE,
  EMPLOYEES_NAME,
  FAILED_FETCH_MESSAGE,
  FULL,
  HR,
  LEAVE_CATEGORY,
  LEAVE_ERROR,
  MANAGE_EMP_LEAVE,
  NAME,
  NUMBER_OF_DAYS,
  PENDING,
  RANGE,
  REASON,
  SINGLE,
  SUBMIT,
  SUBMITING,
  TYPE_OF_LEAVE,
  UPDATE,
  UPDATE_EMP_LEAVE,
  UPDATE_LEAVE,
} from "../../common/constant/Leave";
import BaseButton from "../BaseComponents/BaseButton";
import { BaseSelect } from "../BaseComponents/BaseSelect";

const LeaveModal = ({ isOpen, onClose, editData, onSuccess }) => {
  const role = getRole();
  const name = getName();
  const id = getId();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [defaultDates, setDefaultDates] = useState([]);
  const [listOfCompOffData, setListOfCompOffData] = useState([]);
  const [selectedCompOffKey, setSelectedCompOffKey] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [dateError, setDateError] = useState("");
  const [calendarMode, setCalendarMode] = useState("range");

  const getFilteredLeaveTypes = (category) => {
    if (category === COL) {
      return LEAVE_TYPES.filter((type) => type.value === FULL);
    }
    return LEAVE_TYPES;
  };

  const [filteredLeaveTypes, setFilteredLeaveTypes] = useState(LEAVE_TYPES);

  useEffect(() => {
    if (editData?.fromDate && editData?.toDate) {
      setDefaultDates([new Date(editData.fromDate), new Date(editData.toDate)]);
    }
  }, [editData]);

  const listDataOfCompOfLeave = useCallback((userId = id) => {
    setLoading(true);
    listOfData({
      modelName: "compOfLeaveModel",
      condition: {
        userId: userId,
        isDeleted: false,
        leaveStatus: "Pending",
        status: "Approved",
      },
      selectionCriteria: ["id", "date", "reason", "status"],
    })
      .then((response) => {
        const transformedData = (response?.data || []).map((item) => ({
          label: item.date + " " + "(" + item.reason + ")",
          value: item.date,
          key: item.id,
        }));

        setListOfCompOffData(transformedData);
      })
      .catch((err) => {
        toast.error("Failed to fetch leaves");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const listofEmployee = useCallback(() => {
    setLoading(true);
    listOfEmployee({
      isDeleted: false,
      role: [EMPLOYEE, HR, BDE],
    })
      .then((response) => {
        const transformedData = (response?.data?.data || []).map((item) => ({
          label: `${item.firstName} ${item.lastName}`,
          value: item.id.toString(),
        }));
        setEmployees(transformedData);
      })
      .catch((err) => {
        toast.error(FAILED_FETCH_MESSAGE);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const leaveValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      categoryLeave: editData?.categoryLeave || "",
      fromDate: editData?.fromDate || "",
      toDate: editData?.toDate || "",
      acquireDate: editData?.acquireDate || "",
      date: editData ? `${editData.fromDate} to ${editData.toDate}` : "",
      numberOfDays: editData?.numberOfDays || "",
      leaveReason: editData?.leaveReason || "",
      leaveType: editData?.leaveType || FULL,
      employeeId: editData?.userId || "",
    },

    validationSchema: Yup.object().shape({
      categoryLeave: Yup.string().required(RequiredField(LEAVE_CATEGORY)),
      leaveType: Yup.string().required(RequiredField(TYPE_OF_LEAVE)),
      date: Yup.string().required(RequiredField(DATES)),
      leaveReason: Yup.string().required(RequiredField(REASON)),
      numberOfDays: Yup.number().required(RequiredField(NUMBER_OF_DAYS)),
      employeeId:
        role == "HR" && location.pathname == "/leaves"
          ? Yup.string().required(RequiredField(EMPLOYEES_NAME))
          : Yup.string(),
      acquireDate: Yup.string().when("categoryLeave", {
        is: "COL",
        then: () => Yup.string().required(RequiredField(ACQUIRED_DATE)),
      }),
    }),
    onSubmit: (values) => {
      setLoading(true);

      const payloadLeave = {
        ...(editData?.id && { leaveId: editData.id }),
        userId:
          (role === HR || role === Roles.Admin) && location.pathname == "/leaves"
            ? values.employeeId
            : id,
        fromDate: values?.fromDate,
        toDate: values?.toDate,
        numberOfDays: values?.numberOfDays,
        leaveReason: values?.leaveReason,
        leaveType: values?.leaveType,
        categoryLeave: values?.categoryLeave,
        status: PENDING,
        ...(values.categoryLeave === COL && {
          acquireDate: values?.acquireDate,
          compOfLeaveId: selectedCompOffKey,
        }),
      };

      let apiCall;
      if (editData) {
        apiCall = editLeave(payloadLeave);
      } else {
        apiCall = addLeave(payloadLeave);
      }
      apiCall
        .then((response) => {
          if (response?.data?.statusCode === ResponseStatusEnum?.SUCCESS) {
            toast.success(response?.data?.message);
            leaveValidation.resetForm();
            onClose();
            onSuccess();
          } else {
            toast.error(response?.data?.message);
          }
        })
        .catch((err) => {
          console.error(LEAVE_ERROR, err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  useEffect(() => {
    if (
      role === HR &&
      location.pathname === "/leaves" &&
      leaveValidation.values.employeeId
    ) {
      listDataOfCompOfLeave(leaveValidation.values.employeeId);
    } else {
      listDataOfCompOfLeave();
    }
  }, [
    listDataOfCompOfLeave,
    leaveValidation.values.employeeId,
    role,
    location.pathname,
  ]);

  useEffect(() => {
    if (role === HR || role === ADMIN) {
      listofEmployee();
    }
  }, [listofEmployee, role]);

  const calculateDaysBetweenDates = (start, end) => {
    if (!start || !end) return 0;

    const startDate = new Date(start);
    const endDate = new Date(end);
    if (
      leaveValidation.values.leaveType !== FULL &&
      startDate.getTime() === endDate.getTime()
    ) {
      return 0.5;
    }
    const timeDiff = endDate.getTime() - startDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1;
    return daysDiff;
  };

  const handleEmployeeChange = (selectedOption) => {
    leaveValidation.setFieldValue("employeeId", selectedOption || "");
    leaveValidation.validateField("employeeId");
    if (role === HR && location.pathname === "/leaves") {
      leaveValidation.setFieldValue("acquireDate", "");
      setSelectedCompOffKey(null);
      if (selectedOption) {
        listDataOfCompOfLeave(selectedOption);
      } else {
        setListOfCompOffData([]);
      }
    }
  };

  const handleLeaveCategoryChange = (selectedOption) => {
    leaveValidation.setFieldValue("categoryLeave", selectedOption || "");
    if (selectedOption === COL) {
      leaveValidation.setFieldValue("leaveType", FULL);
      leaveValidation.setFieldValue("fromDate", "");
      leaveValidation.setFieldValue("toDate", "");
      leaveValidation.setFieldValue("date", "");
      leaveValidation.setFieldValue("numberOfDays", "");
      setDefaultDates([]);
    }
  };

  const handleAcquireDateChange = (selectedOption) => {
    leaveValidation.setFieldValue("acquireDate", selectedOption || "");
    if (selectedOption) {
      leaveValidation.setFieldValue("acquireDate", selectedOption);
      const selectedKey = listOfCompOffData?.find((item)=> item.value === selectedOption)?.key;
      setSelectedCompOffKey(selectedKey || null);
    }
  };

  const handleLeaveTypeChange = (e) => {
    leaveValidation.handleChange(e);
    if (e.target.value !== FULL && leaveValidation.values.fromDate) {
      leaveValidation.setFieldValue("toDate", leaveValidation.values.fromDate);
      leaveValidation.setFieldValue("numberOfDays", 0.5);
      setDefaultDates([new Date(leaveValidation.values.fromDate)]);
      leaveValidation.setFieldValue("date", leaveValidation.values.fromDate);
    }
  };

  const handleNumberOfDaysChange = (e) => {
    const value = e.target.value;
    if (value >= 0) {
      leaveValidation.handleChange(e);
    }
  };

  const handleCancelClick = () => {
    onClose();
    leaveValidation.resetForm();
    setDefaultDates([]);
  };

  const handleSingleDateSelection = (selectedDate) => {
    const formattedDate = dateFormatChange(selectedDate);
    leaveValidation.setFieldValue("fromDate", formattedDate);
    leaveValidation.setFieldValue("toDate", formattedDate);
    leaveValidation.setFieldValue("date", formattedDate);
    leaveValidation.setFieldValue(
      "numberOfDays",
      leaveValidation.values.leaveType === FULL ? 1 : 0.5
    );
    setDefaultDates([selectedDate]);
    leaveValidation.setFieldError("date", undefined);
    leaveValidation.setTouched({ ...leaveValidation.touched, date: false });
  };

  const handleDateRangeSelection = (startDate, endDate) => {
    const fromDate = dateFormatChange(startDate);
    const toDate = dateFormatChange(endDate);
    leaveValidation.setFieldValue("fromDate", fromDate);
    leaveValidation.setFieldValue("toDate", toDate);
    leaveValidation.setFieldValue("date", `${fromDate} to ${toDate}`);
    const days = calculateDaysBetweenDates(fromDate, toDate);
    leaveValidation.setFieldValue("numberOfDays", days);
    setDefaultDates([startDate, endDate]);
    leaveValidation.setFieldError("date", undefined);
    leaveValidation.setTouched({ ...leaveValidation.touched, date: false });
  };

  const handleDateChange = (selectedDates) => {
    setDateError("");
    if (calendarMode === SINGLE && selectedDates.length === 1) {
      handleSingleDateSelection(selectedDates[0]);
    } else if (calendarMode === "range" && selectedDates.length === 2) {
      handleDateRangeSelection(selectedDates[0], selectedDates[1]);
    }
  };

  const getFlatpickrOptions = () => {
    return {
      mode: calendarMode,
      altInput: true,
      altFormat: "F j, Y",
      dateFormat: "Y-m-d",
      defaultDate: defaultDates,
      disableMobile: true,
      onChange: handleDateChange,
    };
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    leaveValidation.handleSubmit();
    return false;
  };

  useEffect(() => {
    const isSingleDateMode =
      leaveValidation.values.categoryLeave === COL ||
      leaveValidation.values.leaveType !== FULL;

    setCalendarMode(isSingleDateMode ? SINGLE : RANGE);
    if (isSingleDateMode && defaultDates.length > 1) {
      const singleDate = defaultDates[0];
      setDefaultDates([singleDate]);
      const formattedDate = dateFormatChange(singleDate);
      leaveValidation.setFieldValue("fromDate", formattedDate);
      leaveValidation.setFieldValue("toDate", formattedDate);
      leaveValidation.setFieldValue("date", formattedDate);
      leaveValidation.setFieldValue(
        "numberOfDays",
        leaveValidation.values.leaveType === FULL ? 1 : 0.5
      );
    }
  }, [leaveValidation.values.categoryLeave, leaveValidation.values.leaveType]);

  useEffect(() => {
    const category = leaveValidation.values.categoryLeave;
    const filteredTypes = getFilteredLeaveTypes(category);
    setFilteredLeaveTypes(filteredTypes);
    if (category === COL) {
      leaveValidation.setFieldValue("leaveType", FULL);
    }
  }, [leaveValidation.values.categoryLeave]);

  const findSelectedOption = (options, value) => {
    return options.find((option) => option.value === value) || null;
  };

  useEffect(() => {
    if (editData?.fromDate && editData?.toDate) {
      const fromDate = new Date(editData.fromDate);
      const toDate = new Date(editData.toDate);
      setDefaultDates([fromDate, toDate]);
      leaveValidation.setFieldValue(
        "date",
        `${editData.fromDate} to ${editData.toDate}`
      );
      const daysDiff = calculateDaysBetweenDates(
        editData.fromDate,
        editData.toDate
      );
      leaveValidation.setFieldValue("numberOfDays", daysDiff);
    } else {
      setDefaultDates([]);
    }
  }, [editData]);

  useEffect(() => {
    const leaveType = leaveValidation.values.leaveType;
    const fromDate = leaveValidation.values.fromDate;
    const toDate = leaveValidation.values.toDate;

    if (fromDate && toDate) {
      if (leaveType !== FULL) {
        leaveValidation.setFieldValue("toDate", fromDate);
        leaveValidation.setFieldValue("numberOfDays", 0.5);
        leaveValidation.setFieldValue("date", fromDate);
        setDefaultDates([new Date(fromDate)]);
      } else {
        const days = calculateDaysBetweenDates(fromDate, toDate);
        leaveValidation.setFieldValue("numberOfDays", days);
      }
    }
  }, [leaveValidation.values.leaveType]);

  useEffect(() => {
    if (
      editData &&
      editData.userId &&
      role === HR &&
      location.pathname === "/leaves"
    ) {
      listDataOfCompOfLeave(editData.userId);
    }
  }, [editData, role, location.pathname, listDataOfCompOfLeave]);

  if (!isOpen) return null;

  return (
    <Modal
      id="createTask"
      isOpen={isOpen}
      toggle={onClose}
      modalClassName="zoomIn"
      centered
      tabIndex="-1"
    >
      <ModalHeader toggle={onClose} className="p-3 bg-soft-success">
        {editData?.id
          ? role == HR
            ? UPDATE_EMP_LEAVE
            : UPDATE_LEAVE
          : location.pathname == "/leaves"
          ? MANAGE_EMP_LEAVE
          : APPLY_LEAVE}
      </ModalHeader>
      <ModalBody>
        <Form id="creattask-form" onSubmit={handleFormSubmit}>
          {[ADMIN, HR].includes(role) && location.pathname == "/leaves" && (
            <div className="mb-2">
              <BaseSelect
                label={EMPLOYEES_NAME}
                name="employeeId"
                options={employees}
                placeholder={getSelectPlaceholder(EMPLOYEES_NAME)}
                value={leaveValidation.values.employeeId}
                handleChange={(field, value) => {
                  handleEmployeeChange(value);
                }}
                handleBlur={leaveValidation.handleBlur}
                touched={leaveValidation.touched.employeeId}
                error={leaveValidation.errors.employeeId}
                required={true}
              />
            </div>
          )}

          {!role || role == EMPLOYEE ? (
            <BaseInput
              label={NAME}
              name="name"
              type="text"
              placeholder={getInputPlaceholder(NAME)}
              value={name}
              disabled
            />
          ) : null}

          <BaseSelect
            label={LEAVE_CATEGORY}
            name="categoryLeave"
            options={LEAVE_CATEGORIES}
            placeholder={getSelectPlaceholder(LEAVE_CATEGORY)}
            value={leaveValidation.values.categoryLeave}
            handleChange={(field, value) => {
              handleLeaveCategoryChange(value);
            }}
            handleBlur={leaveValidation.handleBlur}
            touched={leaveValidation.touched.categoryLeave}
            error={leaveValidation.errors.categoryLeave}
            required={true}
          />
          <div className="my-3">
            {leaveValidation.values.categoryLeave === "COL" && (
              <BaseSelect
                label={ACQUIRED_DATE}
                name="acquireDate"
                options={listOfCompOffData}
                placeholder={getSelectPlaceholder(ACQUIRED_DATE)}
                value={leaveValidation.values.acquireDate}
                handleChange={(field, value) => {
                  handleAcquireDateChange(value);
                }}
                handleBlur={leaveValidation.handleBlur}
                touched={leaveValidation.touched.acquireDate}
                error={leaveValidation.errors.acquireDate}
                required={true}
              />
            )}
          </div>

          <div className="my-3">
            <BaseRadioGroup
              label={TYPE_OF_LEAVE}
              name="leaveType"
              options={filteredLeaveTypes}
              value={leaveValidation.values.leaveType}
              onChange={handleLeaveTypeChange}
              handleBlur={leaveValidation.handleBlur}
              error={leaveValidation.errors.leaveType}
              touched={leaveValidation.touched.leaveType}
              disabled={leaveValidation.values.categoryLeave === COL}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="task-duedate-input" className="form-label">
              Date <span className="text-danger">*</span>
            </label>
            <Flatpickr
              className={`form-control ${
                (leaveValidation.errors.date && leaveValidation.touched.date) ||
                dateError
                  ? "is-invalid"
                  : ""
              }`}
              name="date"
              id="date-field"
              value={defaultDates}
              handleBlur={leaveValidation.handleBlur}
              placeholder="Select Date"
              options={getFlatpickrOptions()}
              disabled={editData?.id}
            />

            {leaveValidation.errors.date && leaveValidation.touched.date && (
              <div className="invalid-feedback">
                {leaveValidation.errors.date}
              </div>
            )}

            {dateError && (
              <div className="text-danger small mt-1">{dateError}</div>
            )}
          </div>

          <BaseInput
            label={NUMBER_OF_DAYS}
            name="numberOfDays"
            type="number"
            placeholder={getInputPlaceholder(NUMBER_OF_DAYS)}
            value={leaveValidation.values.numberOfDays}
            onChange={handleNumberOfDaysChange}
            onBlur={leaveValidation.handleBlur}
            error={leaveValidation.errors.numberOfDays}
            touched={leaveValidation.touched.numberOfDays}
            disabled={true}
          />

          <BaseInput
            label={REASON}
            name="leaveReason"
            type="textarea"
            placeholder={getInputPlaceholder(REASON)}
            value={leaveValidation.values.leaveReason}
            onChange={leaveValidation.handleChange}
            handleBlur={leaveValidation.handleBlur}
            error={leaveValidation.errors.leaveReason}
            touched={leaveValidation.touched.leaveReason}
            rows={3}
          />

          <div className="hstack gap-2 justify-content-end">
            <BaseButton
              type="button"
              className="btn btn-light"
              onClick={handleCancelClick}
            >
              Cancel
            </BaseButton>
            <BaseButton type="submit" className="btn btn-primary" id="addNewTodo">
              {loading ? SUBMITING : editData?.id ? UPDATE : SUBMIT}
            </BaseButton>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default LeaveModal;
